import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Drawer from "@mui/material/Drawer";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import {
  useQuery,
  useQueries,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { asMoney } from "@bw/shared/types";
import { useAuth } from "@bw/shared/hooks";
import {
  createStaffInvitation,
  completeTeamPersonOffboarding,
  listStaffInvitations,
  setTeamPersonStaffManagement,
  createClubCoachProfile,
  archiveClubCoachProfile,
  listClubCoachProfiles,
  publishClubCoachProfile,
  updateClubCoachProfile,
  listTeamPeople,
  replaceTeamPersonAssignments,
  suspendTeamPerson,
  getTeamPersonOffboarding,
  type TeamPerson,
  type ClubCoachProfileDraft,
  type StaffOffboardingChecklist,
  type StaffOffboardingWorkItem,
} from "@/libs/staffApi";
import {
  getGrantablePermissions,
  listGrantableFacilityRoles,
} from "@/libs/permissionsApi";
import { financeApi, isApiError } from "@bw/shared/api";
import type { FinanceContextResponse } from "@bw/shared/types/generated";
import { PeopleFinanceAccess } from "./people-finance-access";
import { OrganizationOwnersPanel } from "./OrganizationOwnersPanel";
import { PendingInvitationsPanel } from "./PendingInvitationsPanel";
import { StaffUserPicker } from "./StaffUserPicker";
import { listOrganizationOwners } from "@/libs/organizationOwnersApi";
import type { FacilityListItem } from "@/libs/facilityApi";
import {
  PeopleCommandBoundary,
  usePeopleCommandMemory,
  useRequiredPeopleCommandMemory,
} from "./people-command-lifetime";

type PeopleTabProps = {
  orgId: string;
  facilityId: string | null;
  facilityName: string | null;
  facilities: FacilityListItem[];
  financeContext: FinanceContextResponse | null;
  staffManagementAllowed: boolean;
};

const emptyForm = {
  display_name: "",
  email: "",
  staff_management_enabled: false,
};

function FinanceOnlyPeople({ orgId, context }: { orgId: string; context: FinanceContextResponse | null }) {
  const roster = useQuery({ queryKey: ["finance-people", orgId, context?.binding_generation, context?.authority_generation],
    queryFn: () => listTeamPeople(orgId), enabled: context?.can_manage_finance_access === true, retry: false });
  if (!context?.can_manage_finance_access) return <Alert severity="info">Current owner finance access is required.</Alert>;
  if (roster.isPending) return <Typography role="status">Loading people…</Typography>;
  if (roster.isError) return <Alert severity="error">People are unavailable.</Alert>;
  return <Stack spacing={2}>{roster.data?.length ? roster.data.map(member => <Paper key={member.team_member_id} variant="outlined" sx={{ p: 2 }}>
    <Typography variant="subtitle1">{member.display_name}</Typography><PeopleFinanceAccess member={member} context={context} api={financeApi}
      onChanged={() => { void roster.refetch(); }} /></Paper>) : <Typography>No team members.</Typography>}</Stack>;
}

export function PeopleTab(props: PeopleTabProps) {
  // An organization is the form's authority/lifetime boundary. Old async
  // results may update only their old cache scope, never this new workspace.
  const memory = usePeopleCommandMemory();
  if (!props.staffManagementAllowed) return <FinanceOnlyPeople orgId={props.orgId} context={props.financeContext} />;
  return memory ? (
    <PeopleWorkspace key={props.orgId} {...props} />
  ) : (
    <PeopleCommandBoundary key={props.orgId}>
      <PeopleWorkspace {...props} />
    </PeopleCommandBoundary>
  );
}

function PeopleWorkspace({
  orgId,
  facilityId,
  facilityName,
  facilities = [],
  financeContext,
}: PeopleTabProps) {
  const navigate = useNavigate();
  const cache = useQueryClient();
  const { user } = useAuth();
  // Share the actor-scoped read with PendingInvitationsPanel.
  const invitationsRead = useQuery({
    queryKey: ["staff-invitations", orgId, user?.uid],
    queryFn: () => listStaffInvitations(orgId),
    enabled: Boolean(user),
    retry: false,
  });
  const scope = ["staff-people", orgId] as const;
  const queryOptions = { retry: false, refetchOnWindowFocus: false } as const;
  const peopleRead = useQuery({
    queryKey: [...scope, "people"],
    queryFn: () => listTeamPeople(orgId),
    ...queryOptions,
  });
  const ownerRead = useQuery({
    queryKey: [...scope, "owner-capability"],
    queryFn: () => getGrantablePermissions(orgId),
    ...queryOptions,
  });
  const roleReads = useQueries({
    queries: facilities.map((facility) => ({
      queryKey: [...scope, "grantable-roles", facility.facility_id],
      queryFn: () => listGrantableFacilityRoles(facility.facility_id),
      ...queryOptions,
    })),
  });
  const isOwner =
    ownerRead.isSuccess &&
    !ownerRead.isFetching &&
    ownerRead.data.is_owner === true;
  const ownersRead = useQuery({
    queryKey: ["organization-owners", orgId, user?.uid],
    queryFn: () => listOrganizationOwners(orgId),
    enabled: isOwner,
    retry: false,
  });
  const [search, setSearch] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [ownersOpen, setOwnersOpen] = useState(false);
  const [invitationsOpen, setInvitationsOpen] = useState(false);
  const currentOwners = isOwner && ownersRead.isSuccess && !ownersRead.isFetching ? ownersRead.data : [];
  const roleQuery = (id: string) =>
    roleReads[facilities.findIndex((item) => item.facility_id === id)];
  const roleChoices = (id: string) => roleQuery(id)?.data ?? [];
  const rolesReady = (id: string) => {
    const query = roleQuery(id);
    return (
      query?.isSuccess &&
      !query.isFetching &&
      !query.error &&
      query.data.length > 0
    );
  };
  const profilesRead = useQuery({
    queryKey: [...scope, "coach-profiles"],
    queryFn: () => listClubCoachProfiles(orgId),
    ...queryOptions,
  });
  const people = peopleRead.data ?? [];
  const searchTerm = search.trim().toLowerCase();
  const visiblePeople = people.filter(person => `${person.display_name} ${person.duties.map(duty => `${duty.role_name} ${duty.facility_name}`).join(" ")}`.toLowerCase().includes(searchTerm));
  const ownerOnlyRows = currentOwners.filter(owner => !people.some(person => person.personal_user_id === owner.user_id)).map(owner => ({
    ...owner, name: owner.user_id === user?.uid ? user.displayName || "You" : "Organization owner",
  })).filter(owner => owner.name.toLowerCase().includes(searchTerm));
  const coachProfiles = profilesRead.data ?? [];
  const commands = useRequiredPeopleCommandMemory();
  const {
    createKey,
    createIntent,
    suspendIntent,
    busy,
    working,
    setWorking,
    createOpen,
    setCreateOpen,
    invitationTarget,
    setInvitationTarget,
    suspendTarget,
    setSuspendTarget,
    form,
    setForm,
    selectedUser,
    setSelectedUser,
    inviteByEmail,
    setInviteByEmail,
    facilityRoles,
    setFacilityRoles,
    notice,
    setNotice,
    error,
    setError,
  } = commands;
  // These snapshots have only the open form's lifetime. They are never placed
  // in a Query mutation/cache, storage, log, URL or retry queue.
  const [accessTarget, setAccessTarget] = useState<TeamPerson | null>(null);
  const [coachTarget, setCoachTarget] = useState<TeamPerson | null>(null);
  const [coachProfileTarget, setCoachProfileTarget] =
    useState<ClubCoachProfileDraft | null>(null);
  const [coachBio, setCoachBio] = useState("");
  const [coachSpecializations, setCoachSpecializations] = useState("");
  const [coachFacilityIds, setCoachFacilityIds] = useState<string[]>([]);
  const [coachRates, setCoachRates] = useState<Record<string, string>>({});
  const [offboarding, setOffboarding] =
    useState<StaffOffboardingChecklist | null>(null);
  const [offboardingRevision, setOffboardingRevision] = useState<number | null>(null);
  const terminalOffboardingReady = offboarding?.terminal_ready === true;
  const terminalOffboardingCompleted =
    offboarding?.terminal_completed === true;
  const unavailableOffboarding = (
    memberId: string,
    status: TeamPerson["status"],
  ): StaffOffboardingChecklist => ({
    team_member_id: memberId,
    org_id: orgId,
    access_revoked: status === "suspended" || status === "ended",
    generated_at: new Date().toISOString(),
    items: [],
    unresolved_accepted_lessons: 0,
    complete: false,
    has_more: false,
    source_failures: ["coach", "program", "lesson_request", "social"],
    continuation: null,
    terminal_ready: status === "suspended",
    terminal_completed: status === "ended",
    terminal_blockers: [],
  });

  const load = async () => {
    await Promise.all([
      peopleRead.refetch({ throwOnError: true }),
      ownerRead.refetch({ throwOnError: true }),
      ...roleReads.map((query) => query.refetch({ throwOnError: true })),
      profilesRead.refetch({ throwOnError: true }),
      cache.invalidateQueries({ queryKey: ["staff-invitations", orgId] }),
      cache.invalidateQueries({ queryKey: ["organization-owners", orgId] }),
    ]);
  };

  const closeCreate = () => {
    if (busy.current) return;
    setCreateOpen(false);
    setInvitationTarget(null);
    setForm(emptyForm);
    setSelectedUser(null);
    setInviteByEmail(false);
    setFacilityRoles({});
    createKey.current = null;
    createIntent.current = null;
  };

  const closeSuspend = () => {
    if (busy.current) return;
    setSuspendTarget(null);
    suspendIntent.current = null;
  };

  const create = async () => {
    if (busy.current || (form.staff_management_enabled && !isOwner)) return;
    if (!createIntent.current && (inviteByEmail ? !form.email.trim() : !selectedUser || selectedUser.status !== "available")) return;
    const assignments = Object.entries(facilityRoles).flatMap(
      ([assignedFacilityId, roleIds]) => roleIds.map(roleId => ({
        facility_id: assignedFacilityId,
        role_id: roleId,
      })),
    );
    if (!invitationTarget && (
      (!assignments.length && !form.staff_management_enabled) ||
      assignments.some(
        (assignment) =>
          !rolesReady(assignment.facility_id) ||
          !roleChoices(assignment.facility_id).some(
            (role) => role.role_id === assignment.role_id,
          ),
      )
    ))
      return;
    setError(null);
    busy.current = true;
    setWorking(true);
    const retrying = createKey.current !== null;
    createKey.current ??= crypto.randomUUID();
    const recipient = inviteByEmail ? { email: form.email } : { personal_user_id: selectedUser!.user_id };
    const intent = createIntent.current ??= invitationTarget ? {
      ...recipient,
      team_member_id: invitationTarget.team_member_id,
      expected_member_revision: invitationTarget.session_revision,
      expires_in_days: 7,
      staff_management_enabled: false,
    } : { ...recipient, ...(inviteByEmail ? { display_name: form.display_name } : {}), staff_management_enabled: form.staff_management_enabled, assignments, expires_in_days: 7 };
    try {
      await createStaffInvitation(
        orgId,
        intent,
        createKey.current,
      );
      void cache.invalidateQueries({ queryKey: [...scope, "people"] });
      void cache.invalidateQueries({ queryKey: ["staff", "my-invitations"] });
      void cache.invalidateQueries({ queryKey: ["staff-invitations", orgId] });
      setCreateOpen(false);
      setInvitationTarget(null);
      setForm(emptyForm);
      setSelectedUser(null);
      setInviteByEmail(false);
      setFacilityRoles({});
      createKey.current = null;
      createIntent.current = null;
      setNotice(
        "Invitation created. They can review it from their BW workspaces. Access starts only after they accept.",
      );
    } catch (error) {
      // Only a first request's pre-admission validation rejection proves there
      // is no operation to recover. A rejection after an uncertain attempt
      // cannot invalidate its receipt or justify a new command/key.
      if (!retrying && isApiError(error) && error.status === 404) {
        createKey.current = null;
        createIntent.current = null;
        setSelectedUser(null);
        setError("This account is unavailable. Find another account or check their exact email.");
      } else if (!retrying && isApiError(error) && error.status === 422) {
        createKey.current = null;
        createIntent.current = null;
        setError(
          "Check the name, account email, and assigned roles, then try again.",
        );
      } else {
        setError(
          "The invitation could not be confirmed. Retry this unchanged request before creating another invitation.",
        );
      }
    } finally {
      busy.current = false;
      setWorking(false);
    }
  };

  const suspend = async () => {
    if (!suspendTarget || busy.current) return;
    setError(null);
    suspendIntent.current ??= {
      memberId: suspendTarget.team_member_id,
      key: crypto.randomUUID(),
    };
    const attempt = suspendIntent.current;
    const memberId = attempt.memberId;
    let confirmedRevision: number | null = null;
    busy.current = true;
    setWorking(true);
    try {
      if (!suspendTarget.personal_user_id)
        throw new Error("An accepted BW account membership is required");
      const member = await suspendTeamPerson(
        orgId,
        memberId,
        suspendTarget.session_revision,
      );
      if (member.status !== "suspended")
        throw new Error("Suspension was not confirmed");
      confirmedRevision = member.session_revision;
      setNotice(
        "Business access is suspended. Their personal account is unchanged. Review the remaining work below.",
      );
      suspendIntent.current = null;
    } catch {
      setError("Could not suspend this person. Try again.");
      return;
    } finally {
      busy.current = false;
      setWorking(false);
    }
    setSuspendTarget(null);
    setOffboardingRevision(confirmedRevision);
    try {
      await load();
    } catch {
      setError(
        "Access remains suspended, but People could not be refreshed. Try again.",
      );
    }
    try {
      setOffboarding(await getTeamPersonOffboarding(orgId, memberId));
    } catch {
      setOffboarding(unavailableOffboarding(memberId, "suspended"));
      setError(
        "Access remains suspended. Coverage details could not be loaded, but you can still remove this person now and review unassigned work afterward.",
      );
    }
  };

  const reviewOffboarding = async (person: TeamPerson) => {
    setError(null);
    setOffboardingRevision(person.session_revision);
    try {
      setOffboarding(
        await getTeamPersonOffboarding(orgId, person.team_member_id),
      );
    } catch {
      setOffboarding(unavailableOffboarding(
        person.team_member_id,
        person.status,
      ));
      setError(
        "Coverage details could not be loaded. Removal remains available for a suspended member; review unassigned work afterward.",
      );
    }
  };

  const loadMoreOffboarding = async () => {
    if (!offboarding?.continuation) return;
    try {
      const next = await getTeamPersonOffboarding(
        orgId,
        offboarding.team_member_id,
        offboarding.continuation,
      );
      const items = [...offboarding.items, ...next.items];
      const merged = [
        ...new Map(
          items.map((item) => [`${item.kind}:${item.resource_id}`, item]),
        ).values(),
      ];
      setOffboarding({
        ...next,
        items: merged,
        unresolved_accepted_lessons: merged.filter(
          (item) => item.kind === "accepted_lesson",
        ).length,
      });
    } catch {
      setError(
        "Access remains suspended, but more offboarding items could not be loaded.",
      );
    }
  };

  const closeOffboarding = () => {
    setOffboarding(null);
    setOffboardingRevision(null);
  };

  const finishOffboarding = async () => {
    if (!offboarding || working) return;
    const person = people.find(
      (item) => item.team_member_id === offboarding.team_member_id,
    );
    if (!person && offboardingRevision === null) {
      setError("This team member changed. Close the review and try again.");
      return;
    }
    setWorking(true);
    setError(null);
    try {
      const ended = await completeTeamPersonOffboarding(
        orgId,
        offboarding.team_member_id,
        offboardingRevision ?? person!.session_revision,
      );
      if (ended.status !== "ended")
        throw new Error("Membership completion was not confirmed");
      setOffboarding(null);
      setOffboardingRevision(null);
      setNotice(
        "Team access is fully removed. Their BW account and historical business records are unchanged.",
      );
      await load();
    } catch (cause) {
      setError(
        isApiError(cause)
          ? cause.message
          : "Offboarding could not be completed. Refresh the checklist and try again.",
      );
    } finally {
      setWorking(false);
    }
  };

  const openOffboardingItem = async (item: StaffOffboardingWorkItem) => {
    if (item.action === "reassign_class")
      navigate(`/programs/${item.resource_id}`);
    else if (item.action === "resolve_accepted_lesson")
      navigate(`/coaching/${item.resource_id}`);
    else if (item.action === "reassign_inquiry")
      navigate(`/inquiries/${item.resource_id}`);
    else {
      try {
        await archiveClubCoachProfile(orgId, item.resource_id);
        await load();
        if (offboarding) {
          setOffboarding(
            await getTeamPersonOffboarding(orgId, offboarding.team_member_id),
          );
        }
        setNotice("Coach profile archived and future availability cleared.");
      } catch {
        setError(
          "Could not archive this coach profile. Access remains suspended.",
        );
      }
      return;
    }
    closeOffboarding();
  };

  const editAccess = async () => {
    if (busy.current) return;
    const assignments = Object.entries(facilityRoles).flatMap(
      ([assignedFacilityId, roleIds]) => roleIds.map(roleId => ({
        facility_id: assignedFacilityId,
        role_id: roleId,
      })),
    );
    if (
      !accessTarget ||
      assignments.some(
        (assignment) =>
          !rolesReady(assignment.facility_id) ||
          !roleChoices(assignment.facility_id).some(
            (role) => role.role_id === assignment.role_id,
          ),
      )
    )
      return;
    setError(null);
    busy.current = true;
    setWorking(true);
    try {
      await replaceTeamPersonAssignments(
        orgId,
        accessTarget.team_member_id,
        assignments,
        accessTarget.session_revision,
      );
      setAccessTarget(null);
      setFacilityRoles({});
      setNotice("Facility access updated.");
      await load();
    } catch {
      setError("Could not update facility access. Try again.");
    } finally {
      busy.current = false;
      setWorking(false);
    }
  };

  const setStaffManagement = async (person: TeamPerson, enabled: boolean) => {
    if (
      !isOwner ||
      busy.current ||
      person.status !== "active" || !person.personal_user_id
    )
      return;
    busy.current = true;
    setWorking(true);
    setError(null);
    try {
      const saved = await setTeamPersonStaffManagement(
        orgId,
        person.team_member_id,
        enabled,
        person.session_revision,
      );
      if (
        saved.org_id !== orgId ||
        saved.team_member_id !== person.team_member_id
      )
        throw new Error("Invalid staff response");
      // This is the confirmed server result, not an optimistic permission.
      // Preserve it even when the subsequent roster refresh is unavailable.
      cache.setQueryData<TeamPerson[]>([...scope, "people"], (people) =>
        people?.map((member) =>
          member.team_member_id === saved.team_member_id ? saved : member,
        ),
      );
      setNotice(
        !saved.staff_management_enabled && saved.staff_management_effective
          ? "Explicit permission removed. Other organization grants still provide staff-management access."
          : saved.staff_management_enabled
            ? "Club staff-management permission enabled."
            : "Club staff-management permission removed.",
      );
      await cache.invalidateQueries({ queryKey: scope });
    } catch {
      setError(
        "Could not update staff-management permission. The saved permission is unchanged.",
      );
    } finally {
      busy.current = false;
      setWorking(false);
    }
  };

  const closeCoachEditor = () => {
    setCoachTarget(null);
    setCoachProfileTarget(null);
    setCoachBio("");
    setCoachSpecializations("");
    setCoachFacilityIds([]);
    setCoachRates({});
  };

  const openCoachEditor = (
    person: TeamPerson,
    profile?: ClubCoachProfileDraft,
  ) => {
    setCoachTarget(person);
    setCoachProfileTarget(profile ?? null);
    setCoachBio(profile?.bio ?? "");
    setCoachSpecializations(profile?.specializations.join(", ") ?? "");
    setCoachFacilityIds(
      (profile?.facilities ?? []).map((binding) => binding.facility_id).length
        ? (profile?.facilities ?? []).map((binding) => binding.facility_id)
        : person.duties.map((assignment) => assignment.facility_id),
    );
    setCoachRates(
      Object.fromEntries(
        (profile?.facilities ?? []).map((binding) => [
          binding.facility_id,
          binding.hourly_rate?.toString() ?? "",
        ]),
      ),
    );
  };

  const saveCoachProfile = async () => {
    if (!coachTarget) return;
    setError(null);
    try {
      const body = {
        display_name: coachTarget.display_name,
        bio: coachBio.trim() || null,
        specializations: coachSpecializations
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
        facilities: coachFacilityIds.map((assignedFacilityId) => ({
          facility_id: assignedFacilityId,
          hourly_rate: asMoney(coachRates[assignedFacilityId]),
          currency: "usd",
        })),
      };
      if (coachProfileTarget) {
        await updateClubCoachProfile(
          orgId,
          coachProfileTarget.coach_profile_id,
          body,
        );
        setNotice("Coaching profile updated.");
      } else {
        await createClubCoachProfile(orgId, {
          ...body,
          team_member_id: coachTarget.team_member_id,
        });
        setNotice(
          "Coaching profile draft created. It is not visible to players until published.",
        );
      }
      closeCoachEditor();
      await load();
    } catch {
      setError(
        "Could not save the coaching profile. Check its facilities and rates, then try again.",
      );
    }
  };

  const publishCoach = async (profile: ClubCoachProfileDraft) => {
    setError(null);
    try {
      await publishClubCoachProfile(orgId, profile.coach_profile_id);
      setNotice(
        "Coaching profile published. Players can now discover it when its facility offering is ready.",
      );
      await load();
    } catch {
      setError(
        "Could not publish the coaching profile. Confirm the person is active and try again.",
      );
    }
  };

  return (
    <Stack spacing={2} sx={{ pt: 3 }}>
      {notice && !selectedMemberId ? <Alert severity="info">{notice}</Alert> : null}
      {error && !selectedMemberId ? <Alert severity="error">{error}</Alert> : null}
      {peopleRead.error ? (
        <Alert severity="error">
          Could not load People. Refresh to try again.
        </Alert>
      ) : null}
      {roleReads.some((query) => query.error) || profilesRead.error ? (
        <Alert severity="error">
          Some roles or coaching profiles could not be loaded. Refresh before
          editing them.
        </Alert>
      ) : null}
      {peopleRead.isPending ? (
        <Typography role="status">Loading people…</Typography>
      ) : null}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" } }}>
        <TextField size="small" label="Find a member" value={search}
          onChange={event => setSearch(event.target.value)} sx={{ flex: 1 }} />
        <Button
          disabled={
            working ||
            (!isOwner && (!facilities.length ||
            !roleReads.some(
              (query) =>
                query.isSuccess &&
                !query.isFetching &&
                !query.error &&
                query.data.length > 0,
            )))
          }
          onClick={() => {
            setInvitationTarget(null);
            setFacilityRoles(facilityId ? { [facilityId]: [] } : {});
            setCreateOpen(true);
          }}
          variant="contained"
        >
          Invite member
        </Button>
        <Button
          size="small"
          disabled={working}
          onClick={() =>
            void load().catch(() =>
              setError("Could not refresh People. Try again."),
            )
          }
        >
          Refresh
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        <Button size="small" onClick={() => setInvitationsOpen(true)}>Invitations{invitationsRead.isSuccess ? ` (${invitationsRead.data.filter(item => item.status === "pending").length})` : ""}</Button>
        {isOwner ? <Button size="small" onClick={() => setOwnersOpen(true)}>Manage owners</Button> : null}
      </Stack>
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        {ownerOnlyRows.map(owner => {
          const name = owner.name;
          return <Stack key={owner.user_id} direction="row" spacing={2} sx={{ p: 2, alignItems: "center", borderBottom: 1, borderColor: "divider" }}>
            <Avatar sx={{ width: 36, height: 36 }}>{name.slice(0, 1)}</Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}><Typography sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{name}{owner.user_id === user?.uid && name !== "You" ? " (you)" : ""}</Typography><Typography variant="body2" color="text.secondary">Owner</Typography></Box>
            <Button size="small" onClick={() => setOwnersOpen(true)}>Manage access</Button>
          </Stack>;
        })}
        {visiblePeople.map(person => {
          const pending = invitationsRead.data?.some(item => item.team_member_id === person.team_member_id && item.status === "pending");
          const owner = currentOwners.some(item => item.user_id === person.personal_user_id);
          const lifecycleLabel = person.status === "suspended"
            ? "Suspended"
            : person.status === "ended"
              ? "Removed"
              : null;
          const statusLabel = pending
            ? "Invitation sent"
            : lifecycleLabel
              ?? "Pending";
          return <Stack key={person.team_member_id} direction="row" spacing={2} sx={{ p: 2, alignItems: "center", borderBottom: 1, borderColor: "divider", "&:last-child": { borderBottom: 0 } }}>
            <Avatar sx={{ width: 36, height: 36 }}>{person.display_name.slice(0, 1)}</Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{person.display_name}</Typography>
              <Typography variant="body2" color="text.secondary">{owner ? "Owner" : person.staff_management_effective ? "Manager" : "Staff"}{person.duties.length ? ` · ${person.duties.map(duty => duty.role_name).filter((role, index, roles) => roles.indexOf(role) === index).join(", ")}` : ""}</Typography>
              {pending || person.status !== "active" || !person.personal_user_id ? <Chip size="small" variant="outlined"
                color={person.status === "suspended" ? "warning" : "default"}
                label={statusLabel}
                sx={{ mt: 0.5 }} /> : null}
            </Box>
            <Button size="small" aria-label={`Manage access for ${person.display_name}`} onClick={() => setSelectedMemberId(person.team_member_id)}>Manage access</Button>
          </Stack>;
        })}
        {!peopleRead.isPending && people.length === 0 && !currentOwners.length ? <Typography color="text.secondary" sx={{ p: 3 }}>No members yet. Invite someone to get started.</Typography> : null}
        {!peopleRead.isPending && searchTerm && !visiblePeople.length && !ownerOnlyRows.length && (people.length > 0 || currentOwners.length > 0) ? <Typography color="text.secondary" sx={{ p: 3 }}>No matching members. Try another name, role, or facility.</Typography> : null}
      </Paper>
      <Dialog open={ownersOpen && isOwner} onClose={() => setOwnersOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Organization owners</DialogTitle>
        <DialogContent><OrganizationOwnersPanel orgId={orgId} members={people} /></DialogContent>
        <DialogActions><Button onClick={() => setOwnersOpen(false)}>Done</Button></DialogActions>
      </Dialog>
      <Dialog open={invitationsOpen} onClose={() => setInvitationsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Invitations</DialogTitle><DialogContent><PendingInvitationsPanel orgId={orgId} />
          {invitationsRead.isSuccess && !invitationsRead.data.some(item => item.status === "pending") ? <Typography color="text.secondary">No pending invitations.</Typography> : null}
        </DialogContent>
        <DialogActions><Button onClick={() => setInvitationsOpen(false)}>Done</Button></DialogActions>
      </Dialog>
      <Drawer anchor="right" open={Boolean(selectedMemberId)} onClose={() => !working && setSelectedMemberId(null)}
        slotProps={{ paper: { "aria-label": "Member access", sx: { width: { xs: "100%", sm: 480 }, p: 3 } } }}>
        <Stack spacing={2}>
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}><Typography variant="h6">Member access</Typography><Button disabled={working} onClick={() => setSelectedMemberId(null)}>Done</Button></Stack>
          {notice ? <Alert severity="info">{notice}</Alert> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
      {people.filter(person => person.team_member_id === selectedMemberId).map((person) => {
        const pendingInvitation = invitationsRead.data?.find(
          (item) => item.team_member_id === person.team_member_id && item.status === "pending",
        );
        const coachProfile = coachProfiles.find(
          (profile) => profile.team_member_id === person.team_member_id,
        );
        return (
          <Paper key={person.team_member_id} variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction="column"
              spacing={1}
              sx={{
                alignItems: "stretch",
                justifyContent: "space-between",
              }}
            >
              <div>
                <Typography>{person.display_name}</Typography>
                <Typography color="text.secondary" variant="body2">
                  {person.personal_user_id ? "BW account ready" : "Account invitation pending"} · {person.status}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {new Set(person.duties.map(duty => duty.facility_id)).size} facilit{new Set(person.duties.map(duty => duty.facility_id)).size === 1 ? "y" : "ies"}
                  {coachProfile ? ` · Coaching ${coachProfile.status}` : ""}
                </Typography>
                {isOwner ? (
                  <FormControlLabel
                    label="Manage club staff"
                    control={
                      <Checkbox
                        checked={person.staff_management_enabled}
                        disabled={
                          working ||
                          person.status !== "active" || !person.personal_user_id
                        }
                        onChange={(_, checked) =>
                          void setStaffManagement(person, checked)
                        }
                      />
                    }
                  />
                ) : null}
                {!person.staff_management_enabled && person.staff_management_effective ? (
                  <Typography color="text.secondary" variant="body2">Other organization roles still provide staff-management access.</Typography>
                ) : null}
              </div>
              {!person.personal_user_id && pendingInvitation ? (
                <Button onClick={() => navigate(`/staff/invitations/${encodeURIComponent(pendingInvitation.invitation_id)}`)}>Review invitation</Button>
              ) : null}
              {person.status === "active" && person.personal_user_id ? (
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                  {!coachProfile ? (
                    <Button onClick={() => openCoachEditor(person)}>
                      Set up coaching
                    </Button>
                  ) : (
                    <Button
                      onClick={() => openCoachEditor(person, coachProfile)}
                    >
                      Edit coaching
                    </Button>
                  )}
                  {coachProfile?.status === "draft" ? (
                    <Button onClick={() => void publishCoach(coachProfile)}>
                      Publish coaching
                    </Button>
                  ) : null}
                  <Button
                    onClick={() => {
                      setAccessTarget(person);
                      setFacilityRoles(
                        person.duties.reduce<Record<string, string[]>>((roles, assignment) => {
                          (roles[assignment.facility_id] ??= []).push(assignment.role_id);
                          return roles;
                        }, {}),
                      );
                    }}
                  >
                    Edit access
                  </Button>
                  <Button
                    color="warning"
                    disabled={working}
                    onClick={() => {
                      suspendIntent.current = null;
                      setSuspendTarget(person);
                    }}
                  >
                    Suspend
                  </Button>
                </Stack>
              ) : person.status !== "pending" && person.status !== "active" ? (
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                  <Button onClick={() => void reviewOffboarding(person)}>
                    Review offboarding
                  </Button>
                </Stack>
              ) : null}
            </Stack>
            {financeContext && <PeopleFinanceAccess member={person} context={financeContext} api={financeApi}
              onChanged={() => { void peopleRead.refetch(); }} />}
          </Paper>
        );
      })}
        </Stack>
      </Drawer>
      <Dialog open={createOpen} onClose={closeCreate}>
        <DialogTitle>Invite member</DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          <Stack spacing={2} sx={{ pt: 1, width: { xs: "100%", sm: 360 } }}>
            {!inviteByEmail ? <StaffUserPicker orgId={orgId} value={selectedUser} onChange={setSelectedUser} disabled={working || Boolean(createKey.current)} /> : <><TextField
              label="Display name"
              disabled={working || Boolean(createKey.current)}
              value={form.display_name}
              onChange={(e) =>
                setForm({ ...form, display_name: e.target.value })
              }
            />
            <TextField
              label="BW account email"
              disabled={working || Boolean(createKey.current)}
              type="email"
              helperText="They should sign up for a BW account first. You never need their password."
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            </>}
            <Button size="small" sx={{ alignSelf: "flex-start" }} disabled={working || Boolean(createKey.current)} onClick={() => { setInviteByEmail(!inviteByEmail); setSelectedUser(null); setForm({ ...form, email: "", display_name: "" }); }}>
              {inviteByEmail ? "Search existing accounts" : "Use exact email instead"}
            </Button>
            <Typography variant="body2">Facility access and roles</Typography>
            {isOwner ? (
              <FormControlLabel
                label="Manage club staff"
                control={
                  <Checkbox
                    checked={form.staff_management_enabled}
                    disabled={working || Boolean(createKey.current)}
                    onChange={(_, checked) =>
                      setForm({ ...form, staff_management_enabled: checked })
                    }
                  />
                }
              />
            ) : null}
            {form.staff_management_enabled && !isOwner ? (
              <Alert severity="info">
                Confirm owner access before delegating staff management. Refresh
                permissions, or cancel and start a new form.
              </Alert>
            ) : null}
            <Stack spacing={1}>
              {facilities.map((item) => {
                const selected = item.facility_id in facilityRoles;
                return (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    key={item.facility_id}
                    spacing={1}
                    sx={{ alignItems: { xs: "stretch", sm: "center" } }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={
                            working ||
                            Boolean(createKey.current) ||
                            !rolesReady(item.facility_id)
                          }
                          checked={selected}
                          onChange={(_, checked) =>
                            setFacilityRoles((current) => {
                              const next = { ...current };
                              if (checked) next[item.facility_id] = [];
                              else delete next[item.facility_id];
                              return next;
                            })
                          }
                        />
                      }
                      label={item.name}
                    />
                    <TextField
                      disabled={
                        !selected ||
                        working ||
                        Boolean(createKey.current) ||
                        !rolesReady(item.facility_id)
                      }
                      select
                      size="small"
                      label="Roles at this facility"
                      slotProps={{ select: { multiple: true, renderValue: (value) => <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>{(value as string[]).map(id => <Chip key={id} size="small" label={roleChoices(item.facility_id).find(role => role.role_id === id)?.name ?? "Unavailable role"} />)}</Stack> } }}
                      sx={{ minWidth: 180 }}
                      value={facilityRoles[item.facility_id] ?? []}
                      onChange={(event) =>
                        setFacilityRoles((current) => ({
                          ...current,
                          [item.facility_id]: typeof event.target.value === "string" ? event.target.value.split(",").filter(Boolean) : event.target.value,
                        }))
                      }
                    >
                      {roleChoices(item.facility_id).map((role) => (
                        <MenuItem key={role.role_id} value={role.role_id}>
                          <Checkbox checked={(facilityRoles[item.facility_id] ?? []).includes(role.role_id)} size="small" />{role.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                );
              })}
            </Stack>
            <Typography color="text.secondary" variant="body2">
              Choose one or more roles per facility. Their permissions add together.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" disabled={working} onClick={closeCreate}>
            Cancel
          </Button>
          <Button
            disabled={
              working ||
              (form.staff_management_enabled && !isOwner) ||
              (inviteByEmail ? !form.display_name.trim() || !form.email.trim() : !selectedUser || selectedUser.status !== "available") ||
              (!invitationTarget && ((!Object.keys(facilityRoles).length && !form.staff_management_enabled) ||
              Object.values(facilityRoles).some((roleIds) => !roleIds.length) ||
              Object.entries(facilityRoles).some(
                ([id, roleIds]) =>
                  !rolesReady(id) ||
                  roleIds.some(roleId => !roleChoices(id).some((role) => role.role_id === roleId)),
              )))
            }
            onClick={() => void create()}
            size="small"
            variant="contained"
          >
            Create invitation
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(suspendTarget)} onClose={closeSuspend}>
        <DialogTitle>Suspend person?</DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          <Typography>
            They will lose Business access immediately. Review future work
            afterward.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" disabled={working} onClick={closeSuspend}>
            Cancel
          </Button>
          <Button
            color="warning"
            disabled={working}
            size="small"
            onClick={() => void suspend()}
            variant="contained"
          >
            Suspend
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(offboarding)}
        onClose={closeOffboarding}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Offboarding review</DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert
              severity={offboarding?.access_revoked ? "success" : "warning"}
            >
              {offboarding?.access_revoked
                ? "Business access is revoked. The items below do not restore access."
                : "Suspend access before completing offboarding."}
            </Alert>
            {offboarding && !offboarding.complete ? (
              <Alert severity="warning">
                Some coverage details are unavailable. This does not restore
                access or block removal; refresh the business coverage queue
                afterward.
                {offboarding.has_more
                  ? " More items exist; load the next page below."
                  : ""}
                {offboarding.source_failures.length
                  ? ` Unavailable: ${offboarding.source_failures.join(", ")}.`
                  : ""}
              </Alert>
            ) : null}
            {offboarding?.unresolved_accepted_lessons ? (
              <Alert severity="info">
                {offboarding.unresolved_accepted_lessons} accepted coaching
                agreement
                {offboarding.unresolved_accepted_lessons === 1 ? "" : "s"}{" "}
                will keep the original quote and payment history. Current
                delivery moves to Needs coach so the business can assign a
                replacement without cancelling the player’s purchase.
              </Alert>
            ) : null}
            {terminalOffboardingCompleted ? (
              <Alert severity="success">
                This team membership is already removed. The BW account,
                receipts, and historical business records remain intact.
              </Alert>
            ) : null}
            {terminalOffboardingReady && !terminalOffboardingCompleted ? (
              <Alert severity="success">
                {offboarding?.items.length
                  ? "You can remove this person now. Future classes, private sessions, and inquiries will move to the business’s unassigned work queues."
                  : "You can remove this person now. No future work needs reassignment."}
                {" "}Their BW account, receipts, and historical business records
                will remain intact.
              </Alert>
            ) : null}
            {offboarding?.items.length === 0 && offboarding.complete ? (
              terminalOffboardingCompleted ? (
                <Typography color="text.secondary">
                  No retained future work needs review.
                </Typography>
              ) : !terminalOffboardingReady ? (
                <Alert severity="warning">
                  Future work is clear, but final removal is not ready.
                  {(offboarding.terminal_blockers ?? []).includes("ownership")
                      ? " Transfer organization ownership first."
                      : " Refresh the review after the remaining prerequisite is resolved."}
                </Alert>
              ) : null
            ) : null}
            {offboarding?.items.length ? (
              offboarding?.items.map((item) => (
                <Paper
                  key={`${item.kind}:${item.resource_id}`}
                  variant="outlined"
                  sx={{ p: 2 }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: { sm: "center" },
                    }}
                  >
                    <div>
                      <Typography sx={{ fontWeight: 700 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.kind.replaceAll("_", " ")}
                        {item.starts_at
                          ? ` · ${new Date(item.starts_at).toLocaleString()}`
                          : ""}
                      </Typography>
                      {item.blocking_reason ? (
                        <Typography variant="body2" color="text.secondary">
                          {item.kind === "accepted_lesson"
                            ? "The original quote remains unchanged. Assign a current coach from the business coverage queue."
                            : item.blocking_reason}
                        </Typography>
                      ) : null}
                    </div>
                    <Button onClick={() => void openOffboardingItem(item)}>
                      {item.action === "archive_profile"
                        ? "Archive profile"
                        : "Review"}
                    </Button>
                  </Stack>
                </Paper>
              ))
            ) : null}
            {offboarding?.continuation ? (
              <Button
                variant="outlined"
                onClick={() => void loadMoreOffboarding()}
              >
                Load more work
              </Button>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOffboarding}>Close</Button>
          {terminalOffboardingReady ? (
            <Button
              variant="contained"
              color="error"
              disabled={working}
              onClick={() => void finishOffboarding()}
            >
              {working ? "Finishing…" : "Finish removal"}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(accessTarget)}
        onClose={() => setAccessTarget(null)}
      >
        <DialogTitle>Edit facility access</DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          <Stack spacing={2} sx={{ pt: 1, minWidth: 0, width: { xs: "100%", sm: 360 } }}>
            <Typography variant="body2">Facility access and roles</Typography>
            <Stack spacing={1}>
              {facilities.map((item) => {
                const selected = item.facility_id in facilityRoles;
                return (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    key={item.facility_id}
                    spacing={1}
                    sx={{ alignItems: { xs: "stretch", sm: "center" } }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={working || !rolesReady(item.facility_id)}
                          checked={selected}
                          onChange={(_, checked) =>
                            setFacilityRoles((current) => {
                              const next = { ...current };
                              if (checked) next[item.facility_id] = [];
                              else delete next[item.facility_id];
                              return next;
                            })
                          }
                        />
                      }
                      label={item.name}
                    />
                    <TextField
                      disabled={
                        !selected || working || !rolesReady(item.facility_id)
                      }
                      select
                      size="small"
                      label="Roles at this facility"
                      slotProps={{ select: { multiple: true, renderValue: (value) => <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>{(value as string[]).map(id => <Chip key={id} size="small" label={roleChoices(item.facility_id).find(role => role.role_id === id)?.name ?? "Unavailable role"} />)}</Stack> } }}
                      sx={{ minWidth: 180 }}
                      value={facilityRoles[item.facility_id] ?? []}
                      onChange={(event) =>
                        setFacilityRoles((current) => ({
                          ...current,
                          [item.facility_id]: typeof event.target.value === "string" ? event.target.value.split(",").filter(Boolean) : event.target.value,
                        }))
                      }
                    >
                      {roleChoices(item.facility_id).map((role) => (
                        <MenuItem key={role.role_id} value={role.role_id}>
                          <Checkbox checked={(facilityRoles[item.facility_id] ?? []).includes(role.role_id)} size="small" />{role.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                );
              })}
            </Stack>
            <Typography color="text.secondary" variant="body2">
              Permissions add together across selected roles and groups. Removing a
              role keeps any access granted elsewhere. This changes only direct facility roles.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccessTarget(null)}>Cancel</Button>
          <Button
            disabled={
              Object.values(facilityRoles).some((roleIds) => !roleIds.length) ||
              working ||
              Object.entries(facilityRoles).some(
                ([id, roleIds]) =>
                  !rolesReady(id) ||
                  roleIds.some(roleId => !roleChoices(id).some((role) => role.role_id === roleId)),
              )
            }
            onClick={() => void editAccess()}
            variant="contained"
          >
            Save access
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(coachTarget)} onClose={closeCoachEditor}>
        <DialogTitle>
          {coachProfileTarget ? "Edit coaching profile" : "Set up coaching"}
        </DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          <Stack spacing={2} sx={{ pt: 1, minWidth: 0, width: { xs: "100%", sm: 360 } }}>
            <Typography color="text.secondary" variant="body2">
              This profile belongs to the club. Player discovery uses only the
              facilities and rates selected here.
            </Typography>
            <TextField
              label="Short bio"
              multiline
              minRows={3}
              value={coachBio}
              onChange={(event) => setCoachBio(event.target.value)}
            />
            <TextField
              label="Specializations"
              helperText="Comma-separated, for example: beginners, doubles strategy"
              value={coachSpecializations}
              onChange={(event) => setCoachSpecializations(event.target.value)}
            />
            <Typography variant="body2">
              Teaching facilities and hourly rates
            </Typography>
            {facilities
              .filter((item) =>
                coachTarget?.duties.some(
                  (assignment) => assignment.facility_id === item.facility_id,
                ),
              )
              .map((item) => (
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                  key={item.facility_id}
                  spacing={1}
                    sx={{ alignItems: { xs: "stretch", sm: "center" } }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={coachFacilityIds.includes(item.facility_id)}
                        onChange={(_, checked) =>
                          setCoachFacilityIds((current) =>
                            checked
                              ? [...new Set([...current, item.facility_id])]
                              : current.filter(
                                  (value) => value !== item.facility_id,
                                ),
                          )
                        }
                      />
                    }
                    label={item.name}
                  />
                  <TextField
                    disabled={!coachFacilityIds.includes(item.facility_id)}
                    label="Hourly rate"
                    onChange={(event) =>
                      setCoachRates((current) => ({
                        ...current,
                        [item.facility_id]: event.target.value,
                      }))
                    }
                    size="small"
                    slotProps={{ htmlInput: { min: 0, step: "0.01" } }}
                    type="number"
                    value={coachRates[item.facility_id] ?? ""}
                  />
                </Stack>
              ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCoachEditor}>Cancel</Button>
          <Button
            disabled={!coachFacilityIds.length}
            onClick={() => void saveCoachProfile()}
            variant="contained"
          >
            {coachProfileTarget ? "Save profile" : "Create draft"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
